// CollapsibleFade.tsx — wraps a feed item so it can animate OUT when completed:
// it fades, lifts, and collapses its height to zero, then calls onExited so the
// list can drop it. New items fade/rise IN. Honors reduced-motion.
import React from 'react';

export function CollapsibleFade({
  exiting,
  onExited,
  children,
}: {
  exiting: boolean;
  onExited: () => void;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = React.useState<number | 'auto'>('auto');

  React.useLayoutEffect(() => {
    if (!exiting) return;
    const el = ref.current;
    if (!el) {
      onExited();
      return;
    }
    // Pin the current height, force reflow, then transition to 0.
    setHeight(el.offsetHeight);
    // next frame → collapse
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setHeight(0)));
    const fallback = window.setTimeout(onExited, 700); // safety if transitionend doesn't fire
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(fallback);
    };
  }, [exiting, onExited]);

  return (
    <div
      ref={ref}
      onTransitionEnd={(e) => {
        if (exiting && e.propertyName === 'max-height') onExited();
      }}
      style={{
        maxHeight: exiting ? (height === 'auto' ? undefined : height) : undefined,
        overflow: exiting ? 'hidden' : undefined,
        opacity: exiting ? 0 : 1,
        transform: exiting ? 'translateY(-10px) scale(0.985)' : 'none',
        transition: exiting
          ? 'max-height .46s cubic-bezier(.4,0,.2,1), opacity .34s ease, transform .42s cubic-bezier(.4,0,.2,1)'
          : 'none',
        animation: exiting ? undefined : 'fadeUp .42s ease',
      }}
    >
      {children}
    </div>
  );
}
